'use client';

// Resumable sessions are kept only in memory. They are bearer credentials scoped to one new object.
export async function uploadReaderFile(file: File, url: string, signal: AbortSignal, onProgress: (percent: number) => void, contentType = 'application/epub+zip'): Promise<string> {
  const chunkBytes = 8 * 1024 * 1024; let offset = 0; let failures = 0;
  function committedOffset(response: Response, minimum: number, maximum: number) {
    const range = response.headers.get('range'); const match = range?.match(/^bytes=0-(\d+)$/);
    if (range && !match) throw new Error('The upload returned an invalid committed range.');
    const value = match ? Number(match[1]) + 1 : 0;
    if (!Number.isSafeInteger(value) || value < minimum || value > maximum || value > file.size) throw new Error('The upload returned an invalid offset.');
    return value;
  }
  async function request(body: Blob, contentRange: string) {
    const controller = new AbortController(); const abort = () => controller.abort(signal.reason);
    signal.addEventListener('abort', abort, { once: true }); if (signal.aborted) abort();
    const timeout = setTimeout(() => controller.abort(new Error('Upload request timed out.')), 60_000);
    try { return await fetch(url, { method: 'PUT', body, headers: { 'Content-Type': contentType, 'Content-Range': contentRange }, signal: controller.signal }); }
    finally { clearTimeout(timeout); signal.removeEventListener('abort', abort); }
  }
  while (offset < file.size) {
    const end = Math.min(file.size, offset + chunkBytes);
    try {
      const response = await request(file.slice(offset, end), `bytes ${offset}-${end - 1}/${file.size}`);
      if (response.ok) { const metadata = await response.json() as { generation?: string }; if (!metadata.generation) throw new Error('Upload completed without an object generation.'); onProgress(100); return metadata.generation; }
      if (response.status !== 308) throw new Error('The upload could not continue.');
      const next = committedOffset(response, offset, end); if (next === offset) throw new Error('The upload made no progress.'); offset = next;
      failures = 0; onProgress(Math.round(offset / file.size * 100));
    } catch (error) {
      if (signal.aborted || ++failures > 3) throw error;
      const status = await request(new Blob([]), `bytes */${file.size}`);
      if (status.ok) { const metadata = await status.json() as { generation?: string }; if (metadata.generation) return metadata.generation; }
      if (status.status !== 308) throw error;
      const next = committedOffset(status, offset, end); if (next > offset) { failures = 0; offset = next; onProgress(Math.round(offset / file.size * 100)); }
    }
  }
  // A final 308 can acknowledge every byte before the object metadata is returned.
  // Bound status probes rather than treating a fully acknowledged upload as lost.
  for (let attempt = 0; attempt < 3; attempt++) {
    const status = await request(new Blob([]), `bytes */${file.size}`);
    if (status.ok) { const metadata = await status.json() as { generation?: string }; if (metadata.generation) { onProgress(100); return metadata.generation; } }
    if (status.status !== 308) break;
    committedOffset(status, file.size, file.size);
  }
  throw new Error('Upload did not return a final object generation.');
}
